import { ConversationInlineQuestionCard } from "@/components/shapes/conversation-frame/conversation-inline-question-card";
import { cn } from "@/lib/cn";
import type { TaskChatKind } from "@/lib/task-chat-kind";
import { InputBar, type InputBarProps } from "./input-bar";
import {
  TASK_CHAT_INPUT_BAR_PROPS,
  useWorkerComposer,
} from "./use-worker-composer";
import { WorkerComposerAlerts } from "./worker-composer-alerts";

/** The existing composer, with a task chat's worker state folded into it. */
export function TaskConversationComposer({
  taskChat,
  taskId,
  ...props
}: InputBarProps & {
  taskChat: TaskChatKind | null;
  taskId?: string;
}) {
  const composer = useWorkerComposer({
    enabled: taskChat !== null,
    projectId: props.projectId,
    taskId,
    onSubmit: props.onSubmit,
    disabled: props.disabled,
    placeholder: props.placeholder,
  });
  // Native and coding-app chats receive attachment paths through their
  // provider channel once the worker snapshot identifies the destination.
  const imagesAllowed = taskChat !== null && composer.workerProvider !== undefined;
  return (
    <div>
      {composer.question ? (
        <ConversationInlineQuestionCard
          key={composer.question.awaitingInputId}
          question={composer.question}
          testId="task-conversation-question"
          className={cn("mb-2", props.className)}
          onAnswered={composer.onQuestionAnswered}
        />
      ) : null}
      <WorkerComposerAlerts {...composer.alerts} />
      <InputBar
        {...props}
        dictationId={`worker-composer:${props.liveDictationComposerId ?? taskId}`}
        disabled={composer.disabled}
        placeholder={composer.placeholder}
        onSubmit={composer.onSubmit}
        workerControls={composer.workerControls}
        {...(taskChat
          ? {
              ...TASK_CHAT_INPUT_BAR_PROPS,
              // Lift only the image lockdown for providers with image
              // follow-ups; slash commands, routing and dictation stay off.
              ...(imagesAllowed
                ? {
                    allowAttachments: true,
                    images: props.images,
                    onImageFile: props.onImageFile,
                    onRemoveImage: props.onRemoveImage,
                  }
                : {}),
              conversationController: props.conversationController
                ? {
                    ...props.conversationController,
                    canStartNewConversation: false,
                  }
                : undefined,
            }
          : {})}
      />
    </div>
  );
}
